import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Minus, Plus, Download, Ruler, ArrowLeft, X } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useCart } from '../../context/CartContext';
import { ProductCard } from '../../components/shop/ProductCard';
import { SHOP_API, CATEGORIES, COLLECTIONS, SIZE_TABLES, img, taka, num, canSwatch } from '../../components/shop/shopUtils';
import { Swatch, ContentsList, SetFields, GiftActions } from '../../components/shop/SetExtras';
import { ShopAnnouncement, useShopSettings } from './Shop';

export const SizeTables = () => (
  <div className="space-y-6">
    {SIZE_TABLES.map((tb) => (
      <div key={tb.title}>
        <h3 className="mb-2 font-semibold">{tb.title} <span className="text-xs font-normal text-[#6B7280]">(cm)</span></h3>
        <div className="overflow-x-auto"><table className="w-full min-w-[420px] border border-[#E5E7EB] text-sm">
          <thead className="bg-[#F9FAFB]"><tr><th className="p-2 text-left">Measurement</th>{tb.cols.map((c) => <th key={c} className="p-2">{c}</th>)}</tr></thead>
          <tbody>{tb.rows.map((r) => <tr key={r[0]} className="border-t border-[#E5E7EB]">{r.map((c, i) => <td key={i} className={`p-2 ${i ? 'text-center' : ''}`}>{c}</td>)}</tr>)}</tbody>
        </table></div>
      </div>
    ))}
  </div>
);

const SizeGuideModal = ({ onClose }) => {
  useEffect(() => {
    const k = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', k);
    return () => document.removeEventListener('keydown', k);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="size-guide-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative max-h-[85vh] w-full max-w-3xl overflow-y-auto rounded-xl bg-white p-6">
        <button onClick={onClose} className="absolute right-4 top-4" aria-label="Close" autoFocus data-testid="size-guide-close"><X className="h-5 w-5" /></button>
        <h2 className="shop-heading mb-4 text-xl">Size guide</h2>
        <SizeTables />
      </div>
    </div>
  );
};

const useSeo = (p) => {
  useEffect(() => {
    if (!p) return undefined;
    document.title = `${p.seo?.title || p.name.en} | NDP Official Store`;
    const ld = document.createElement('script');
    ld.type = 'application/ld+json';
    ld.text = JSON.stringify({ '@context': 'https://schema.org', '@type': 'Product', name: p.name.en, sku: p.sku || p.variants?.[0]?.sku, image: p.images?.map(img), description: p.seo?.desc || p.name.en,
      offers: { '@type': 'Offer', priceCurrency: 'BDT', price: p.price, availability: p.variants?.some((v) => v.stock > 0) ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock' } });
    document.head.appendChild(ld);
    const og = document.createElement('meta');
    og.setAttribute('property', 'og:image');
    og.content = img(p.images?.[0]);
    document.head.appendChild(og);
    return () => { ld.remove(); og.remove(); document.title = 'NDP Bangladesh'; };
  }, [p]);
};

export default function ProductPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const cart = useCart();
  const s = useShopSettings();
  const [data, setData] = useState(undefined);
  const [imgIdx, setImgIdx] = useState(0);
  const [colour, setColour] = useState('');
  const [size, setSize] = useState('');
  const [qty, setQty] = useState(1);
  const [guide, setGuide] = useState(false);
  const [zoom, setZoom] = useState(null);
  const [email, setEmail] = useState('');
  const [notified, setNotified] = useState(false);
  const [extras, setExtras] = useState({ personalisation: '', gift_message: '' });

  useEffect(() => {
    let live = true;
    setData(undefined);
    fetch(`${SHOP_API}/products/${slug}`).then((r) => (r.ok ? r.json() : null)).then((d) => {
      if (!live) return;
      setData(d);
      if (d) {
        const first = d.product.variants.find((v) => v.stock > 0) || d.product.variants[0];
        const pick = d.product.cat === 'women';
        setColour(pick && d.product.colours?.length > 1 ? '' : first?.colour || '');
        setSize(pick && d.product.sizes?.length > 1 ? '' : first?.size || '');
        setExtras({ personalisation: '', gift_message: '' });
        setImgIdx(0);
        setQty(1);
      }
    }).catch(() => live && setData(null));
    return () => { live = false; };
  }, [slug]);

  const p = data?.product;
  useSeo(p);
  const variant = useMemo(() => p?.variants.find((v) => v.colour === colour && v.size === size), [p, colour, size]);
  if (data === undefined) return <div className="py-24 text-center text-[#6B7280]">…</div>;
  if (!p) return <div className="py-24 text-center" data-testid="product-not-found">{t({ en: 'Product not found.', bn: 'পণ্য পাওয়া যায়নি।' })}</div>;

  const paused = s?.paused;
  const images = p.images?.length ? p.images : [null];
  const stock = variant?.stock ?? 0;
  const catLabel = CATEGORIES.find((c) => c.id === p.cat);
  const colLabel = COLLECTIONS.find((c) => c.id === p.col);
  const optBtn = (active, disabled) => `min-w-[3rem] rounded-md border px-3 py-2 text-sm transition-colors ${active ? 'border-[#E31E24] bg-[#FEE2E2] text-[#E31E24]' : 'border-[#E5E7EB] hover:border-[#0A0A0A]'} ${disabled ? 'opacity-40 line-through' : ''}`;
  const sizeStock = (sz) => p.variants.find((v) => v.colour === colour && v.size === sz)?.stock ?? 0;

  const isSet = p.type === 'set';
  const pers = isSet && p.personalise ? extras.personalisation.trim() : '';
  const unitPrice = p.price + (pers ? p.personalise_price || 0 : 0);
  const needPick = !variant;
  const swatches = p.cat === 'women' && p.colours?.every(canSwatch);
  const add = () => cart.add({ slug: p.slug, variant_sku: variant.sku, qty, price: unitPrice, name: p.name, image: images[0], colour, size, max: stock,
    ...(pers ? { personalisation: pers } : {}), ...(isSet && extras.gift_message.trim() ? { gift_message: extras.gift_message.trim() } : {}), ...(p.baby ? { baby: true } : {}) });
  const notify = async () => {
    const r = await fetch(`${SHOP_API}/notify`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ slug: p.slug, variant_sku: variant.sku, email }) });
    if (r.ok) setNotified(true);
  };

  return (
    <div className="bg-white" data-testid="product-page">
      <ShopAnnouncement s={s} />
      <div className="shop-container py-6">
        <button onClick={() => navigate('/shop')} className="mb-4 inline-flex items-center gap-1 text-sm text-[#6B7280] hover:text-[#0A0A0A]" data-testid="back-to-shop"><ArrowLeft className="h-4 w-4" /> {t({ en: 'Back to shop', bn: 'শপে ফিরুন' })}</button>
        <div className="grid gap-8 lg:grid-cols-2">
          <div>
            <div className="relative aspect-[4/3] cursor-zoom-in overflow-hidden rounded-xl bg-[#F3F0EA]" data-testid="product-main-image"
              onMouseMove={(e) => { const r = e.currentTarget.getBoundingClientRect(); setZoom({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 }); }}
              onMouseLeave={() => setZoom(null)} onClick={() => setZoom(zoom ? null : { x: 50, y: 50 })}>
              <img src={img(images[imgIdx])} alt={`${p.name.en} · ${p.name.bn || ''}`} className="h-full w-full object-cover transition-transform duration-200"
                style={zoom ? { transform: 'scale(2)', transformOrigin: `${zoom.x}% ${zoom.y}%` } : undefined} />
            </div>
            {images.length > 1 && (
              <div className="mt-3 flex gap-2 overflow-x-auto">
                {images.slice(0, 7).map((u, i) => (
                  <button key={i} onClick={() => setImgIdx(i)} className={`h-16 w-20 shrink-0 overflow-hidden rounded-md border-2 ${i === imgIdx ? 'border-[#E31E24]' : 'border-transparent'}`} data-testid={`product-thumb-${i}`}><img src={img(u)} alt="" className="h-full w-full object-cover" /></button>
                ))}
              </div>
            )}
            {p.hires && <a href={img(p.hires)} download target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-[#0E9A49] hover:underline" data-testid="download-hires-link"><Download className="h-4 w-4" /> {t({ en: 'Download high-resolution image', bn: 'হাই-রেজোলিউশন ছবি ডাউনলোড' })}</a>}
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]" data-testid="product-labels">{t(colLabel || { en: p.col, bn: p.col })}{p.col.toLowerCase() !== p.cat ? <> · {t(catLabel || { en: p.cat, bn: p.cat })}</> : null}</p>
            <h1 className="shop-heading mt-1 text-3xl" data-testid="product-name">{language === 'bn' ? p.name.bn || p.name.en : p.name.en}</h1>
            <p className="text-[#6B7280]">{language === 'bn' ? p.name.en : p.name.bn}</p>
            {!paused && <p className="mt-3 text-2xl font-semibold" data-testid="product-detail-price">{taka(p.price, language)} {p.compare_at ? <s className="ml-2 text-base font-normal text-[#6B7280]">{taka(p.compare_at, language)}</s> : null}</p>}
            {!paused && (p.note?.[language] || p.note?.en) && <p className="text-sm text-[#6B7280]">{p.note[language] || p.note.en}</p>}

            {p.colours?.length > 0 && (
              <div className="mt-5"><p className="mb-2 text-sm font-medium">{t({ en: 'Colour', bn: 'রং' })}: <span className="text-[#6B7280]">{colour}</span></p>
                <div className={`flex flex-wrap ${swatches ? 'gap-3 pt-1' : 'gap-2'}`} data-testid={swatches ? 'nakshi-swatches' : 'colour-options'}>{p.colours.map((c) => (swatches ? <Swatch key={c} c={c} active={colour === c} onClick={() => setColour(c)} /> : <button key={c} onClick={() => setColour(c)} className={optBtn(colour === c)} data-testid={`colour-option-${c.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{c}</button>))}</div></div>
            )}
            {p.sizes?.length > 0 && (
              <div className="mt-4"><div className="mb-2 flex items-center justify-between"><p className="text-sm font-medium">{t({ en: 'Size', bn: 'সাইজ' })}: <span className="text-[#6B7280]">{size}</span></p>
                {p.cat === 'apparel' || p.cat === 'kids' ? <button onClick={() => setGuide(true)} className="inline-flex items-center gap-1 text-sm text-[#0E9A49] hover:underline" data-testid="open-size-guide"><Ruler className="h-4 w-4" /> {t({ en: 'Size guide', bn: 'সাইজ গাইড' })}</button> : null}</div>
                <div className="flex flex-wrap gap-2">{p.sizes.map((sz) => <button key={sz} onClick={() => setSize(sz)} className={optBtn(size === sz, sizeStock(sz) === 0)} data-testid={`size-option-${sz.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{sz}</button>)}</div></div>
            )}

            {needPick && !paused ? <p className="mt-4 text-sm font-medium text-amber-700" data-testid="pick-options-hint">{(p.sizes?.length ? t({ en: 'Choose a colour and size to add to cart', bn: 'কার্টে যোগ করতে রং ও সাইজ বেছে নিন' }) : t({ en: 'Choose a colour to add to cart', bn: 'কার্টে যোগ করতে রং বেছে নিন' }))}</p> : <p className={`mt-4 text-sm font-medium ${stock === 0 ? 'text-[#E31E24]' : stock <= (variant?.low ?? 5) ? 'text-amber-600' : 'text-[#0E9A49]'}`} data-testid="variant-stock">
              {stock === 0 ? t({ en: 'Out of stock', bn: 'স্টক শেষ' }) : stock <= (variant?.low ?? 5) ? t({ en: `Only ${stock} left`, bn: `মাত্র ${num(stock, 'bn')}টি বাকি` }) : t({ en: 'In stock', bn: 'স্টকে আছে' })}
            </p>}
            {isSet && !paused && <SetFields p={p} extras={extras} setExtras={setExtras} />}

            {paused ? <p className="mt-4 rounded-md bg-[#FEE2E2] p-3 text-sm text-[#E31E24]" data-testid="product-paused">{t(s.pause_message)}</p> : needPick ? (
              <button disabled className="shop-btn mt-4 w-full bg-[#E31E24] text-base text-white opacity-50" data-testid="product-add-to-cart">{t({ en: 'Add to cart', bn: 'কার্টে যোগ করুন' })}</button>
            ) : stock > 0 ? (
              <div className="mt-4 flex gap-3">
                <div className="flex items-center rounded-md border border-[#E5E7EB]">
                  <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-3" aria-label="Decrease quantity" data-testid="qty-dec"><Minus className="h-4 w-4" /></button>
                  <span className="w-10 text-center" data-testid="qty-value">{num(qty, language)}</span>
                  <button onClick={() => setQty(Math.min(stock, qty + 1))} className="p-3" aria-label="Increase quantity" data-testid="qty-inc"><Plus className="h-4 w-4" /></button>
                </div>
                <button onClick={add} className="shop-btn flex-1 bg-[#E31E24] text-base text-white hover:bg-[#c8181d]" data-testid="product-add-to-cart">{t({ en: 'Add to cart', bn: 'কার্টে যোগ করুন' })}{pers && p.personalise_price ? ` · ${taka(unitPrice * qty, language)}` : ''}</button>
              </div>
            ) : notified ? <p className="mt-4 rounded-md bg-[#D1FAE5] p-3 text-sm text-[#0E9A49]" data-testid="notify-success">{t({ en: "We'll email you when it's back.", bn: 'স্টকে এলে আমরা ইমেইল করব।' })}</p> : (
              <div className="mt-4 flex gap-2" data-testid="notify-form">
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="flex-1 rounded-md border border-[#E5E7EB] px-3 py-2 text-sm" data-testid="notify-email" aria-label="Email for stock notification" />
                <button onClick={notify} disabled={!/\S+@\S+\.\S+/.test(email)} className="shop-btn bg-[#0E9A49] text-white disabled:opacity-50" data-testid="notify-submit">{t({ en: 'Notify me', bn: 'জানান' })}</button>
              </div>
            )}

            {p.cat === 'gifts' && !paused && <GiftActions p={p} />}
            {isSet && <ContentsList p={p} />}
            <table className="mt-6 w-full text-sm" data-testid="product-specs">
              <tbody>
                {(p.spec || []).filter(([k]) => !(isSet && k === 'Contents')).map(([k, v], i) => <tr key={i} className="border-t border-[#E5E7EB]"><td className="w-1/3 py-2 pr-3 text-[#6B7280]">{k}</td><td className="py-2">{v}</td></tr>)}
                <tr className="border-t border-[#E5E7EB]"><td className="py-2 pr-3 text-[#6B7280]">SKU</td><td className="py-2 font-mono text-xs" data-testid="product-sku">{variant?.sku || p.sku}</td></tr>
              </tbody>
            </table>
          </div>
        </div>

        {data.related.length > 0 && (
          <section className="mt-14" data-testid="related-products">
            <div className="tricolour-rule mb-6" />
            <h2 className="shop-heading mb-5 text-2xl">{t({ en: 'You may also like', bn: 'আপনার পছন্দ হতে পারে' })}</h2>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">{data.related.map((r) => <ProductCard key={r._id} p={r} paused={paused} />)}</div>
          </section>
        )}
      </div>
      {guide && <SizeGuideModal onClose={() => setGuide(false)} />}
    </div>
  );
}
